import axios from "axios";
import Cookies from "js-cookie";
import { getDomain, generateDuration } from "../../auth/helpers";
import { api } from "../../app/conf";

// Single refresh promise to deduplicate concurrent 401 retries
let refreshPromise = null;

const refreshToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const raw = Cookies.get("session", { domain: getDomain() });
      const session = raw ? JSON.parse(raw) : null;

      if (!session?.refreshToken) throw new Error("No refresh token");

      const response = await axios.post(
        `${api}/access/refresh`,
        {},
        { headers: { "access-token": session.refreshToken } }
      );

      const { access, refresh } = response.data;

      // Update cookie so both crm-web and omuni-web stay in sync
      Cookies.set(
        "session",
        JSON.stringify({
          ...session,
          accessToken: access,
          refreshToken: refresh,
        }),
        { expires: generateDuration(), domain: getDomain() }
      );

      // Keep promise alive briefly for late-arriving requests
      setTimeout(() => { refreshPromise = null; }, 2000);

      return access;
    } catch (error) {
      refreshPromise = null;
      throw error;
    }
  })();

  return refreshPromise;
};

/**
 * Attach a 401 response interceptor to an axios instance.
 * On 401, attempts a single token refresh and retries the original request.
 * If refresh fails, redirects to login.
 */
export const attachResponseInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newToken = await refreshToken();
          originalRequest.headers["X-Access-Token"] = newToken;
          originalRequest.headers["access-token"] = newToken;
          return axios(originalRequest);
        } catch {
          // Refresh failed — session is dead, redirect to login
          Cookies.remove("session", { domain: getDomain(), path: "/" });
          // Only redirect if not already on signin page to prevent loop
          if (!window.location.pathname.includes("/signin")) {
            window.location.href = "/signin";
          }
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
};
